<script lang="ts">
  /**
   * Which discovery language an audit measures in — the same control on the public tool
   * and on Discovery, because it is the same question in both places.
   *
   * IT SHOWS THE COMMUNITY'S OWN. Skool's discovery filters by language and returns a
   * different field for each, so a German community measured in English is told it ranks
   * nowhere — true only of a search its members would never run. Once a run reports what
   * the index says the community is, that is what this displays; picking something else
   * is a deliberate act, and the pick then wins.
   *
   * SEARCHABLE, because Skool offers 48 languages and a 49-item native menu is a scroll
   * hunt. Typing filters; an empty box still lists every option, so the choice is never
   * hidden behind a query.
   *
   * The options come from the API (`GET /audit/languages`), so this file holds no list
   * that could fall out of step with Skool's.
   */
  import { fetchLanguages, type LanguageOption } from '../../audit';

  let {
    value = $bindable(),
    resolved = null,
    disabled = false,
    label = 'Language',
    onchange,
  }: {
    value: string | null;
    /** The language the run reported measuring in — shown until the reader picks. */
    resolved?: string | null;
    disabled?: boolean;
    label?: string;
    /** Called after a different language is picked. The public page carries the choice
     *  into its next run; Discovery re-runs straight away. */
    onchange?: () => void;
  } = $props();

  let options = $state<LanguageOption[]>([]);
  let open = $state(false);
  let query = $state('');
  let host: HTMLElement | null = $state(null);
  let search: HTMLInputElement | null = $state(null);

  $effect(() => {
    void fetchLanguages().then((o) => (options = o));
  });

  // An explicit pick beats the run's own language; before either, say so plainly rather
  // than showing a language nobody chose.
  const selected = $derived(value ?? resolved);
  const shown = $derived(
    options.find((o) => o.value === selected)?.label ??
      (selected ? selected.charAt(0).toUpperCase() + selected.slice(1) : 'Community’s own'),
  );

  const matches = $derived(
    query.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
      : options,
  );

  function pick(v: string): void {
    value = v;
    open = false;
    query = '';
    onchange?.();
  }

  function toggle(): void {
    if (disabled) return;
    open = !open;
    query = '';
    if (open) queueMicrotask(() => search?.focus());
  }

  function onkey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      open = false;
      query = '';
    } else if (e.key === 'Enter' && matches.length > 0) {
      e.preventDefault();
      pick(matches[0]!.value);
    }
  }

  // Close on a click anywhere else. Bound only while open, so the page carries no
  // listener for a menu nobody has opened.
  $effect(() => {
    if (!open) return;
    const away = (e: MouseEvent): void => {
      if (host && !host.contains(e.target as Node)) open = false;
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  });
</script>

<div class="relative inline-flex items-center gap-2 text-sm" bind:this={host}>
  <!-- `whitespace-nowrap`: the label is two words and wrapped onto its own line inside a
       flex row that had room for it. -->
  <span class="whitespace-nowrap text-base-content/60">{label}</span>

  <button
    type="button"
    class="btn btn-outline btn-sm min-w-[11rem] justify-between font-normal"
    onclick={toggle}
    {disabled}
    aria-haspopup="listbox"
    aria-expanded={open}
  >
    <span class="truncate">{shown}</span>
    <span class="opacity-50" aria-hidden="true">&#9662;</span>
  </button>

  {#if open}
    <div
      class="absolute left-0 top-full z-50 mt-1 w-64 rounded-box border border-base-300
             bg-base-100 shadow-lg"
      role="listbox"
      aria-label="Discovery language"
    >
      <div class="p-2">
        <!-- svelte-ignore a11y_autofocus -->
        <input
          bind:this={search}
          bind:value={query}
          onkeydown={onkey}
          class="input input-sm input-bordered w-full"
          type="text"
          placeholder="Search"
          aria-label="Search languages"
        />
      </div>
      <ul class="max-h-64 overflow-y-auto pb-2">
        {#each matches as o (o.value)}
          <li>
            <button
              type="button"
              class="flex w-full items-center justify-between px-3 py-1.5 text-left
                     hover:bg-base-200"
              class:font-semibold={o.value === selected}
              onclick={() => pick(o.value)}
            >
              <span class="truncate">{o.label}</span>
              {#if o.value === selected}<span class="text-primary">&#10003;</span>{/if}
            </button>
          </li>
        {:else}
          <li class="px-3 py-2 text-base-content/50">No language matches “{query}”.</li>
        {/each}
      </ul>
    </div>
  {/if}
</div>
